// contactListService.js

import API_BASE_URL from '../../../../../Constants';
import { combineIntersectionData } from '../utils/contactListUtils';

/**
 * Helper function to fetch data from an API endpoint.
 * @param {string} endpoint - The API endpoint to fetch data from.
 * @param {AbortSignal} signal - The signal to abort the fetch request.
 * @returns {Array|Object} The fetched data.
 */
const fetchData = async (endpoint, signal) => {
  const response = await fetch(endpoint, { credentials: 'include', signal });
  if (!response.ok) {
    throw new Error(`Network response was not ok: ${response.status} ${response.statusText}`);
  }
  const data = await response.json();
  return data;
};

/**
 * Fetches paginated contact data based on the selected list type.
 * @param {string} selectedListType - The type of contacts to fetch.
 * @param {number} page - The page number to fetch.
 * @param {number} size - The number of records per page.
 * @param {AbortSignal} signal - The signal to abort the fetch request.
 * @param {string} searchQuery - The search query string.
 * @returns {Object} An object containing the paginated data, fields, and total pages.
 */
export const fetchPaginatedContacts = async (selectedListType, page, size, signal, searchQuery = '') => {
  let endpoint = '';
  let fields = [];
  let referenceEndpoint = '';
  let referenceKey = '';
  let intersectionKey = '';
  let referenceNameKey = '';
  let newKeyName = '';
  let combineData = false; // Flag to determine if we need to combine data

  switch (selectedListType) {
    case 'allContacts':
      endpoint = `${API_BASE_URL}/api/setup/contacts/get-page?page=${page}&size=${size}&search=${encodeURIComponent(searchQuery)}`;
      fields = [
        { key: 'actions', label: 'Actions' },
        { key: 'contactName', label: 'Contact Name' },
        { key: 'contactLevel', label: 'Contact Level' },
        { key: 'phone1', label: 'Primary Phone Number' },
        { key: 'emailAddress', label: 'Email' },
      ];
      break;

    case 'managementCompanies':
      endpoint = `${API_BASE_URL}/api/setup/mgmtco-contact-intersections/get-page?page=${page}&size=${size}`;
      referenceEndpoint = `${API_BASE_URL}/api/setup/managementcompanies/all`;
      referenceKey = 'managementId';
      intersectionKey = 'managementCompanyId';
      referenceNameKey = 'companyName';
      newKeyName = 'managementCompanyName';
      combineData = true;
      fields = [
        { key: 'actions', label: 'Actions' },
        { key: 'managementCompanyName', label: 'Management Company' },
        { key: 'contactName', label: 'Contact Name' },
        { key: 'phone1', label: 'Primary Phone Number' },
        { key: 'emailAddress', label: 'Email' },
        { key: 'mainContact', label: 'Main Contact' },
        { key: 'agentContact', label: 'Agent Contact' },
        { key: 'collectionsContact', label: 'Collections Contact' },
      ];
      break;

    case 'customers':
      endpoint = `${API_BASE_URL}/api/setup/customer-contact-intersections/get-page?page=${page}&size=${size}`;
      referenceEndpoint = `${API_BASE_URL}/api/setup/customers/all`;
      referenceKey = 'customerID';
      intersectionKey = 'customerId';
      referenceNameKey = 'customerName';
      newKeyName = 'customerName';
      combineData = true;
      fields = [
        { key: 'actions', label: 'Actions' },
        { key: 'customerName', label: 'Customer' },
        { key: 'contactName', label: 'Contact Name' },
        { key: 'phone1', label: 'Primary Phone Number' },
        { key: 'emailAddress', label: 'Email' },
        { key: 'mainContact', label: 'Main Contact' },
        { key: 'agentContact', label: 'Agent Contact' },
        { key: 'collectionsContact', label: 'Collections Contact' },
      ];
      break;

    case 'buildings':
      endpoint = `${API_BASE_URL}/api/setup/building-contact-intersections/get-page?page=${page}&size=${size}`;
      referenceEndpoint = `${API_BASE_URL}/api/setup/buildings/all`;
      referenceKey = 'buildingID';
      intersectionKey = 'buildingId';
      referenceNameKey = 'location';
      newKeyName = 'buildingName';
      combineData = true;
      fields = [
        { key: 'actions', label: 'Actions' },
        { key: 'buildingName', label: 'Building' },
        { key: 'contactName', label: 'Contact Name' },
        { key: 'phone1', label: 'Primary Phone Number' },
        { key: 'emailAddress', label: 'Email' },
        { key: 'mainContact', label: 'Main Contact' },
        { key: 'agentContact', label: 'Agent Contact' },
        { key: 'superintendentContact', label: 'Superintendent Contact' },
        { key: 'collectionsContact', label: 'Collections Contact' },
        { key: 'dobContact', label: 'DOB Contact' },
      ];
      break;

    case 'employees':
      endpoint = `${API_BASE_URL}/api/admin/users/page/?page=${page}&size=${size}`;
      fields = [
        { key: 'id', label: 'Employee ID' },
        { key: 'fullName', label: 'Name' },
        { key: 'email', label: 'Email' },
        { key: 'phone', label: 'Phone' },
        { key: 'role', label: 'Role' },
      ];
      break;

    default:
      throw new Error(`Unsupported list type: ${selectedListType}`);
  }

  // Fetch data
  const responseData = await fetchData(endpoint, signal);
  const content = responseData.content || responseData;
  const totalPages = responseData.totalPages || 1;

  let data = content;

  if (selectedListType === 'employees') {
    // For employees, map over the data to add fullName
    data = data.map((user) => ({
      ...user,
      fullName: `${user.firstName} ${user.lastName}`,
    }));
  } else if (combineData) {
    // Fetch reference data
    const referenceData = await fetchData(referenceEndpoint, signal);

    // Combine data
    data = combineIntersectionData(
      data,
      referenceData,
      intersectionKey,
      referenceKey,
      referenceNameKey,
      newKeyName
    );
  } else if (selectedListType === 'allContacts') {
  // Ensure each contact has a consistent 'contactId' field
  data = data.map(contact => ({
    ...contact,
    contactId: contact.contactId || contact.id || contact.buildingContactId || contact.buildingContactID,
  }));
}

  return { data, fields, totalPages };
};
